import React, { FC } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { orderAssistFaqsDefaultFirst } from 'utils/assist/orderAssistFaqsDefaultFirst';
import {
  AssistMigrationDistributionPreview,
  AssistMigrationSource,
  EscalationRule,
  FAQ,
} from 'types/assist/assistMigration';
import { BusinessSettingsEditor } from './BusinessSettingsEditor';
import { EscalationEditor } from './EscalationEditor';
import { FaqEditor } from './FaqEditor';
import { MigrationComparisonSection } from './MigrationComparisonSection';
import { MigrationSourceValue } from './MigrationSourceValue';
import { StoreSectionEditor } from './StoreSectionEditor';

interface DistributionEditorProps {
  source: AssistMigrationSource | null;
  distribution: AssistMigrationDistributionPreview;
  defaultFaqQuestions?: readonly string[];
  onUpdateBusinessSettings: (
    patch: Partial<AssistMigrationDistributionPreview['businessSettings']>,
  ) => void;
  onUpdateGlobal: (patch: Partial<AssistMigrationDistributionPreview['global']>) => void;
  onUpdateStore: (
    storeId: number,
    patch: Partial<AssistMigrationDistributionPreview['stores'][number]>,
  ) => void;
}

export const DistributionEditor: FC<DistributionEditorProps> = ({
  source,
  distribution,
  defaultFaqQuestions = [],
  onUpdateBusinessSettings,
  onUpdateGlobal,
  onUpdateStore,
}) => {
  const orderedGlobalFaqs = orderAssistFaqsDefaultFirst({
    faqs: distribution.global.faqs,
    defaultFaqQuestions,
  });

  const handleGlobalFaqsChange = (faqs: FAQ[]) => {
    onUpdateGlobal({ faqs });
  };

  const handleGlobalEscalationRulesChange = (escalationRules: EscalationRule[]) => {
    onUpdateGlobal({ escalationRules });
  };

  return (
    <Box
      sx={{
        '& .MuiInputLabel-root': {
          fontSize: '14px',
          transform: 'translate(10px, 7px) scale(1)',
        },
        '& .MuiInputLabel-root.MuiInputLabel-shrink': {
          transform: 'translate(10px, -6px) scale(0.75)',
        },
        '& .MuiOutlinedInput-root': { fontSize: '14px' },
        '& .MuiOutlinedInput-input': { padding: '6px 10px' },
        '& .MuiOutlinedInput-root.MuiInputBase-multiline': { padding: '6px 10px' },
        '& .MuiOutlinedInput-root.MuiInputBase-multiline .MuiOutlinedInput-input': { padding: 0 },
        '& .MuiFormHelperText-root': { fontSize: '0.625rem', marginTop: '2px' },
      }}
    >
      <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 12 }}>
        Settings comparison — {distribution.stores.length} location(s)
      </Typography>

      <MigrationComparisonSection
        title="Welcome message"
        before={<MigrationSourceValue value={source?.welcomeMessage} />}
        after={
          <BusinessSettingsEditor
            setting="welcomeMessage"
            value={distribution.businessSettings}
            onChange={onUpdateBusinessSettings}
          />
        }
      />

      <MigrationComparisonSection
        title="Hours & location"
        before={<MigrationSourceValue value={source?.hoursLocationMessage} />}
        after={
          <StoreSectionEditor
            section="hoursLocation"
            distribution={distribution}
            onUpdateStore={onUpdateStore}
          />
        }
      />

      <MigrationComparisonSection
        title="Services & pricing"
        before={<MigrationSourceValue value={source?.servicesPricingMessage} />}
        after={
          <StoreSectionEditor
            section="servicesPricing"
            distribution={distribution}
            onUpdateStore={onUpdateStore}
          />
        }
      />

      <MigrationComparisonSection
        title="FAQs"
        before={<MigrationSourceValue value={source?.faqs} />}
        after={
          <>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
              Global FAQs
            </Typography>
            <FaqEditor
              items={orderedGlobalFaqs}
              onChange={handleGlobalFaqsChange}
              readOnlyQuestions={defaultFaqQuestions}
            />
            <StoreSectionEditor
              section="faqs"
              distribution={distribution}
              onUpdateStore={onUpdateStore}
            />
          </>
        }
      />

      <MigrationComparisonSection
        title="Escalation"
        before={
          <>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
              Default transfer number
            </Typography>
            <MigrationSourceValue value={source?.escalationPhoneNumber} />
            <Typography
              variant="caption"
              sx={{ display: 'block', fontWeight: 600, mt: 1.5, mb: 0.5 }}
            >
              Escalation protocol
            </Typography>
            <MigrationSourceValue value={source?.escalationProtocol} />
          </>
        }
        after={
          <>
            <BusinessSettingsEditor
              setting="escalationPhoneNumber"
              value={distribution.businessSettings}
              onChange={onUpdateBusinessSettings}
            />
            <Typography
              variant="caption"
              sx={{ display: 'block', fontWeight: 600, mt: 1.5, mb: 0.5 }}
            >
              Global escalation rules
            </Typography>
            <EscalationEditor
              items={distribution.global.escalationRules}
              onChange={handleGlobalEscalationRulesChange}
            />
            <StoreSectionEditor
              section="escalation"
              distribution={distribution}
              onUpdateStore={onUpdateStore}
            />
          </>
        }
      />

      <MigrationComparisonSection
        title="Upsell protocol"
        before={<MigrationSourceValue value={source?.upsellProtocol} />}
        after={
          <Typography variant="body2" color="textSecondary">
            Not supported in Assist 2.0. This setting will not be migrated.
          </Typography>
        }
      />
    </Box>
  );
};
